import {
  UseQueryOptions,
  UseSuspenseQueryOptions
} from "@tanstack/react-query";

import queryClient from "services/queryClient";
import { getClient } from "services/utils/ServiceClientRegistry";
import { UserXp, XpRewardsI } from "services/xpService/types";

import { pushGlobalValuesToDataLayer } from "seneca-common/features/analytics/gtm";
import wrongAnswers from "seneca-common/features/premium-trials/state/reducers/state/trials/wrongAnswers";

export const XP_SERVICE_QUERY_KEY = "xp";
const USER_XP_KEY = "userXp";
const XP_REWARDS_KEY = "xpRewards";

function buildUserXpQueryKey({
  courseId,
  quizId
}: { courseId?: string; quizId?: string } = {}) {
  return [XP_SERVICE_QUERY_KEY, USER_XP_KEY, courseId, quizId].filter(Boolean);
}

export const XpRewardsQueryKey = [XP_SERVICE_QUERY_KEY, XP_REWARDS_KEY];

export type UserXpQueryOptions = {
  courseId?: string;
  quizId?: string;
};

export function getXpQueryOptions(
  options?: UserXpQueryOptions
): UseSuspenseQueryOptions<UserXp> {
  const { courseId, quizId } = options ?? {};
  return {
    queryKey: buildUserXpQueryKey({
      courseId,
      quizId
    }),
    queryFn: async () => {
      const userXp = await getClient("xpService").fetchXP(options);
      pushGlobalValuesToDataLayer({
        totalUserXp: userXp.totalUserXp,
        userLevel: userXp.userLevel.level
      });
      return userXp;
    },
    staleTime: Infinity
  };
}

export function getXpRewardsQueryOptions(): UseQueryOptions<XpRewardsI> {
  return {
    queryKey: XpRewardsQueryKey,
    queryFn: async () => {
      return await getClient("xpService").fetchRewards();
    },
    staleTime: Infinity
  };
}

export function getCachedXpRewards() {
  return queryClient.getQueryData<XpRewardsI>(XpRewardsQueryKey);
}

export function optimisticDecrementEMA() {
  queryClient.setQueryData(XpRewardsQueryKey, (rewards: XpRewardsI) => ({
    ...rewards,
    explainMyAnswer: (rewards.explainMyAnswer ?? 1) - 1
  }));
}

export function optimisticDecrementExamQuestions() {
  queryClient.setQueryData(XpRewardsQueryKey, (rewards: XpRewardsI) => ({
    ...rewards,
    examQuestion: (rewards.examQuestion ?? 1) - 1
  }));
}

export function optimisticDecrementWrongAnswers() {
  queryClient.setQueryData(XpRewardsQueryKey, (rewards: XpRewardsI) => ({
    ...rewards,
    wrongAnswers: (rewards.wrongAnswers ?? 1) - 1
  }));
}
